// src/models/ultrai.ts
var ChartType = /* @__PURE__ */ ((ChartType2) => {
  ChartType2["BAR"] = "bar";
  ChartType2["SCATTERPLOT"] = "scatterplot";
  ChartType2["HEATMAP"] = "heatmap";
  return ChartType2;
})(ChartType || {});
var PeriodEnum = /* @__PURE__ */ ((PeriodEnum2) => {
  PeriodEnum2["DAY"] = "DAY";
  PeriodEnum2["WEEK"] = "WEEK";
  PeriodEnum2["MONTH"] = "MONTH";
  PeriodEnum2["YEAR"] = "YEAR";
  PeriodEnum2["MAX"] = "MAX";
  return PeriodEnum2;
})(PeriodEnum || {});

// src/models/common.ts
var ConditionalType = /* @__PURE__ */ ((ConditionalType2) => {
  ConditionalType2["STOP_LOSS"] = "STOP_LOSS";
  ConditionalType2["TAKE_PROFIT"] = "TAKE_PROFIT";
  return ConditionalType2;
})(ConditionalType || {});

// src/models/cash.ts
var Status = /* @__PURE__ */ ((Status2) => {
  Status2["PENDING"] = "Pending";
  Status2["AUTHORIZED"] = "Authorized";
  Status2["CARD_VERIFIED"] = "Card Verified";
  Status2["VOIDED"] = "Voided";
  Status2["PARTIALLY_CAPTURED"] = "Partially Captured";
  Status2["CAPTURED"] = "Captured";
  Status2["PARTIALLY_REFUNDED"] = "Partially Refunded";
  Status2["REFUNDED"] = "Refunded";
  Status2["DECLINED"] = "Declined";
  Status2["CANCELED"] = "Canceled";
  Status2["EXPIRED"] = "Expired";
  Status2["PAID"] = "Paid";
  return Status2;
})(Status || {});

// src/models/transactions.ts
var TransactionType = /* @__PURE__ */ ((TransactionType2) => {
  TransactionType2["SELL"] = "SELL";
  TransactionType2["BUY"] = "BUY";
  TransactionType2["DIVIDEND_CASH"] = "DIVIDEND_CASH";
  TransactionType2["DIVIDEND_SHARES"] = "DIVIDEND_SHARES";
  TransactionType2["WITHDRAWAL"] = "WITHDRAWAL";
  TransactionType2["DEPOSIT"] = "DEPOSIT";
  TransactionType2["SUBSCRIPTION"] = "SUBSCRIPTION";
  TransactionType2["TRANSFER"] = "TRANSFER";
  TransactionType2["REDEMPTION"] = "REDEMPTION";
  TransactionType2["SAFEKEEPING"] = "SAFEKEEPING";
  TransactionType2["NOTE_DEBIT"] = "NOTE_DEBIT";
  TransactionType2["NOTE_CREDIT"] = "NOTE_CREDIT";
  TransactionType2["WALLET_TRANSFER"] = "WALLET_TRANSFER";
  return TransactionType2;
})(TransactionType || {});
var OrderMethod = /* @__PURE__ */ ((OrderMethod2) => {
  OrderMethod2["MARKET"] = "MARKET";
  OrderMethod2["LIMIT"] = "LIMIT";
  return OrderMethod2;
})(OrderMethod || {});

// src/utils/HttpClient.ts
var HttpError = class extends Error {
  status;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message, status, cause) {
    super(message, { cause });
    this.status = status;
  }
};
var HttpClient = class _HttpClient {
  static instance;
  config;
  /**
   * Private constructor to enforce singleton pattern
   * @param config - Configuration object for HttpClient
   * @private
   */
  constructor(config) {
    this.config = config;
  }
  /**
   * Merges custom config with default headers
   * @param config - Client config
   * @returns Combined request configuration
   * @private
   */
  getHeaders(config) {
    const baseHeaders = {
      "x-api-key": config.apiKey,
      "x-client-version": config.client?.version,
      "x-client-type": config.client?.type,
      "x-client-name": config.client?.name,
      "Accept-Language": config.language || "en",
      // English language set by default
      "Content-Type": "application/json",
      ...config?.extraHeaders || {}
    };
    if (config.authToken) {
      return {
        ...baseHeaders,
        Authorization: `Bearer ${config.authToken}`
      };
    }
    return baseHeaders;
  }
  /**
   * Gets the singleton instance of HttpClient
   * @param config - Configuration object for HttpClient
   * @returns HttpClient instance
   * @static
   */
  static getInstance(config) {
    _HttpClient.instance = new _HttpClient(config);
    return _HttpClient.instance;
  }
  async fetchData(url, method = "GET", body, signal) {
    if (!url) {
      throw new Error(`Missing url: url parameter is mandatory`);
    }
    const headers = this.getHeaders(this.config);
    const requestUrl = `${this.config.baseURL}${url}`;
    try {
      const response = await fetch(requestUrl, {
        method,
        body: body ? JSON.stringify(body) : null,
        headers,
        signal
      });
      if (!response.ok) {
        throw new HttpError(
          `Request failed with status code ${response.status}`,
          response.status,
          response
        );
      }
      const responseData = await response.json();
      return { data: responseData, status: response.status };
    } catch (error) {
      throw error;
    }
  }
  /**
   * Performs a POST request
   * @template T - Request payload type
   * @template Z - Response type
   * @param url - Endpoint URL
   * @param data - Request payload
   * @returns Promise with the response
   */
  async create(url, data, signal) {
    return this.fetchData(url, "POST", data, signal);
  }
  /**
   * Performs a GET request
   * @template Z - Response type
   * @param url - Endpoint URL
   * @returns Promise with the response
   */
  async read(url, signal) {
    return this.fetchData(url, "GET", void 0, signal);
  }
  /**
   * Performs a PUT request
   * @template T - Request payload type
   * @template Z - Response type
   * @param url - Endpoint URL
   * @param data - Request payload
   * @returns Promise with the response
   */
  async update(url, data, signal) {
    return this.fetchData(url, "PUT", data, signal);
  }
  /**
   * Performs a PATCH request
   * @template T - Request payload type
   * @template Z - Response type
   * @param url - Endpoint URL
   * @param data - Request payload
   * @returns Promise with the response
   */
  async patch(url, data, signal) {
    return this.fetchData(url, "PATCH", data, signal);
  }
  /**
   * Performs a DELETE request
   * @template Z - Response type
   * @param url - Endpoint URL
   * @returns Promise with the response
   */
  async delete(url, signal) {
    return this.fetchData(url, "DELETE", void 0, signal);
  }
  /**
   * Performs a HEAD request
   * @template Z - Response type
   * @param url - Endpoint URL
   * @returns Promise with the response
   */
  async head(url, signal) {
    return this.fetchData(url, "HEAD", void 0, signal);
  }
};
var HttpClient_default = HttpClient;

// src/services/BaseService.ts
var BaseService = class {
  httpClient;
  /**
   * Creates a new instance of BaseService
   * @param config - HTTP client configuration options
   */
  constructor(config) {
    this.httpClient = HttpClient_default.getInstance(config);
  }
};
var BaseService_default = BaseService;

// src/services/assessments/AssessmentsService.ts
var AssessmentsService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Saves the customer questionnaire answers
   * @param assessmentId - The ID of the assessment
   * @param answers - The answers to the questionnaire
   * @returns Promise containing the evaluation response
   */
  async saveAnswers(assessmentId, answers) {
    return this.httpClient.update(
      `/api/v1/assessments/${assessmentId}/answers/me`,
      answers
    );
  }
  /**
   * Retrieves the evaluation of the customer questionnaire answers
   * @param assessmentId - The ID of the assessment
   * @returns Promise containing the evaluation response
   */
  async getEvaluation(assessmentId) {
    return this.httpClient.read(
      `/api/v1/assessments/${assessmentId}/answers/me/evaluation`
    );
  }
  /**
   * Retrieves the questionnaire for a specific assessment
   * @param assessmentId - The ID of the assessment
   * @returns Promise containing the questionnaire response
   */
  async getQuestionnaire(assessmentId) {
    return this.httpClient.read(
      `/api/v1/assessments/${assessmentId}/questions`
    );
  }
};
var AssessmentsService_default = AssessmentsService;

// src/services/assets/AssetsService.ts
var AssetsService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Fetches all available funds
   * @returns Promise containing the list of funds
   */
  async fetchFunds() {
    return this.httpClient.read("/api/v1/assets/funds");
  }
  /**
   * Fetches the Fund details by asset ID
   * @param assetId - The ID of the asset to retrieve details for
   * @returns Promise containing the fund details
   */
  async fetchFundDetails(assetId) {
    return this.httpClient.read(
      `/api/v1/assets/funds/${assetId}`
    );
  }
  /**
   * Fetches discovered assets with funds information
   * @returns Promise containing the discovered assets
   */
  async fetchFundsDiscover() {
    return this.httpClient.read(
      "/api/v1/assets/funds/discover"
    );
  }
  /**
   * Creates a Fund preorder
   * @param request - The fund preorder request
   * @returns Promise containing the fund preorder response
   */
  async createFundPreOrder(request) {
    return this.httpClient.create(
      "/api/v1/assets/funds/pre-orders",
      request
    );
  }
  /**
   * Creates a Fund order
   * @param request - The fund order request
   * @returns Promise containing the fund order response
   */
  async createFundOrder(request) {
    return this.httpClient.create(
      "/api/v1/assets/funds/orders",
      request
    );
  }
  /**
   * Cancels a Fund order
   * @param orderId - The ID of the order to cancel
   * @returns Promise containing the response of the cancellation
   */
  async cancelFundOrder(orderId) {
    return this.httpClient.patch(
      `/api/v1/assets/funds/orders/${orderId}/cancel`
    );
  }
  /**
   * Fetches all available filters for Funds
   * @returns Promise containing the available filters
   */
  async fetchFundsFilters() {
    return this.httpClient.read(
      "/api/v1/assets/funds/filters"
    );
  }
  /**
   * Fetches analyst ratings tips for a specific asset
   * @param assetId - The ID of the asset to retrieve tips for
   * @returns Promise containing the analyst ratings tips response
   */
  async fetchAnalystRatingsTips(assetId) {
    return this.httpClient.read(
      `/api/v1/assets/tips/${assetId}/analyst-ratings`
    );
  }
  /**
   * Fetches forecast tips for a specific asset
   * @param assetId - The ID of the asset to retrieve tips for
   * @returns Promise containing the forecast tips response
   */
  async fetchForecastTips(assetId) {
    return this.httpClient.read(
      `/api/v1/assets/tips/${assetId}/forecast`
    );
  }
  /**
   * Fetches earnings tips for a specific asset
   * @param assetId - The ID of the asset to retrieve tips for
   * @returns Promise containing the earnings tips response
   */
  async fetchEarningsTips(assetId) {
    return this.httpClient.read(
      `/api/v1/assets/tips/${assetId}/earnings-revenues`
    );
  }
  /**
   * Fetches news tips for a specific asset
   * @param assetId - The ID of the asset to retrieve tips for
   * @returns Promise containing the news tips response
   */
  async fetchNewsTips(assetId) {
    return this.httpClient.read(
      `/api/v1/assets/tips/${assetId}/news`
    );
  }
  /**
   * Fetches bulls and bears tips for a specific asset
   * @param assetId - The ID of the asset to retrieve tips for
   * @returns Promise containing the bulls and bears tips response
   */
  async fetchBullsBearsTips(assetId) {
    return this.httpClient.read(
      `/api/v1/assets/tips/${assetId}/bulls-bears`
    );
  }
};
var AssetsService_default = AssetsService;

// src/services/brokerage/BrokerageService.ts
var BrokerageService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  // ------------------- Markets Methods -------------------
  /**
   * Retrieves available markets
   * @returns Promise containing the list of markets
   */
  async getMarkets() {
    return this.httpClient.read("/api/v1/orders/markets");
  }
  /**
   * Retrieves market details by market ID
   * @param marketId - The ID of the market to retrieve
   * @returns Promise containing the market details
   */
  async getMarketDetails(marketId) {
    return this.httpClient.read(`/api/v1/orders/markets/${marketId}`);
  }
  /**
   * Retrieves the price limit for a specific asset
   * @param limitPriceRangeRequest - The request data for limit price range
   * @returns Promise containing the limit price range
   */
  async getLimitPriceRange(limitPriceRangeRequest) {
    return this.httpClient.create(
      "/api/v1/orders/markets/price-limit",
      limitPriceRangeRequest
    );
  }
  /**
   * Retrieves asset operations details
   * @param assetId - The ID of the asset
   * @returns Promise containing the asset operations details
   */
  async getAssetOperations(assetId) {
    return this.httpClient.read(
      `/api/v1/orders/asset/${assetId}/operations`
    );
  }
  // ------------------- Orders Methods -------------------
  /**
   * Retrieves a specific order by ID
   * @param orderId - The ID of the order to retrieve
   * @returns Promise containing the order response
   */
  async getOrderById(orderId) {
    return this.httpClient.read(`/api/v2/orders/${orderId}`);
  }
  /**
   * Cancels an order by ID
   * @param orderId - The ID of the order to cancel
   * @returns Promise containing the order response
   */
  async cancelOrder(orderId) {
    return this.httpClient.create(
      `/api/v1/orders/${orderId}/cancel`
    );
  }
  /**
   * Places an order
   * @param orderRequest - The order request data
   * @returns Promise containing the order response
   */
  async placeOrder(orderRequest) {
    return this.httpClient.create(
      "/api/v2/orders",
      orderRequest
    );
  }
  /**
   * Retrieves client orders with optional status filter
   * @param page - The page number
   * @param size - The size of the page
   * @param status - Optional status of the orders to retrieve
   * @returns Promise containing the paginated orders
   */
  async getOrders(page, size, status) {
    const statusQuery = status ? `&status=${status}` : "";
    return this.httpClient.read(
      `/api/v2/orders?page=${page}&size=${size}${statusQuery}`
    );
  }
  /**
   * Places multiple orders
   * @param ordersRequest - The list of orders to place
   * @returns Promise containing the responses for multiple orders
   */
  async placeMultipleOrders(ordersRequest) {
    return this.httpClient.create(
      "/api/v2/orders/multiple",
      ordersRequest
    );
  }
  // ------------------- PreOrders Methods -------------------
  /**
   * Places a pre-order
   * @param preOrderCommand - The pre-order command data
   * @returns Promise containing the pre-order response
   */
  async placePreOrder(preOrderCommand) {
    return this.httpClient.create(
      "/api/v3/pre-orders",
      preOrderCommand
    );
  }
  /**
   * Places multiple pre-orders
   * @param preOrders - The list of pre-orders to place
   * @returns Promise containing the response for multiple pre-orders
   */
  async placeMultiplePreOrders(preOrders) {
    return this.httpClient.create(
      "/api/v3/pre-orders/multiple",
      preOrders
    );
  }
  // ------------------- Tier Methods -------------------
  /**
   * Retrieves broker tier information
   * @returns Promise containing the broker tier information
   */
  async getBrokerTier() {
    return this.httpClient.read("/api/v1/broker/tier");
  }
};
var BrokerageService_default = BrokerageService;

// src/services/cash/CashService.ts
var CashService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  // ------------------- Cards Methods -------------------
  /**
   * Retrieves the available card payment schemes.
   * @returns Promise containing the response with available schemes
   */
  async getCard() {
    return this.httpClient.read(
      `/api/v1/card-payments/schemes`
    );
  }
  /**
   * Deletes a specific card by its ID.
   * @param cardId - The ID of the card to delete
   * @returns Promise indicating the result of the deletion
   */
  async deleteCard(cardId) {
    return this.httpClient.delete(`/api/v1/card-payments/cards/${cardId}`);
  }
  // ------------------- Fees Methods -------------------
  /**
   * Retrieves the fees associated with card payments.
   * @returns Promise containing the response with fees information
   */
  async getFees() {
    return this.httpClient.read("/api/v1/card-payments/fees");
  }
  // ------------------- Card Payments Methods -------------------
  /**
   * Creates a new payment using card details.
   * @param payRequest - The payment request containing card information
   * @returns Promise containing the response with payment details
   */
  async createPayment(payRequest) {
    return this.httpClient.create("/api/v1/card-payments/payments", payRequest);
  }
  // ------------------- Recurring Payments Methods -------------------
  /**
   * Creates a new recurring payment.
   * @param recurringPayRequest - The request containing recurring payment details
   * @returns Promise containing the response with recurring payment details
   */
  async createRecurringPayment(recurringPayRequest) {
    return this.httpClient.create("/api/v1/card-payments/recurring-payments", recurringPayRequest);
  }
  /**
   * Lists all recurring payments.
   * @returns Promise containing the response with a list of recurring payments
   */
  async listRecurringPayments() {
    return this.httpClient.read(
      "/api/v1/card-payments/recurring-payments"
    );
  }
  /**
   * Retrieves details of a specific recurring payment by its ID.
   * @param recurringPaymentId - The ID of the recurring payment to retrieve
   * @returns Promise containing the response with recurring payment details
   */
  async getRecurringPayment(recurringPaymentId) {
    return this.httpClient.read(
      `/api/v1/card-payments/recurring-payments/${recurringPaymentId}`
    );
  }
  /**
   * Deletes a specific recurring payment by its ID.
   * @param recurringPaymentId - The ID of the recurring payment to delete
   * @returns Promise indicating the result of the deletion
   */
  async deleteRecurringPayment(recurringPaymentId) {
    return this.httpClient.delete(
      `/api/v1/card-payments/recurring-payments/${recurringPaymentId}`
    );
  }
  // ------------------- Webhooks Methods -------------------
  /**
   * Captures the change status of a payment via webhook.
   * @param webhookRequest - The request payload for the payment status change
   * @returns Promise indicating the result of the capture
   */
  async captureChangeStatusPayment(webhookRequest) {
    return this.httpClient.create(
      "/api/v1/card-payments/checkout-webhook/payments",
      webhookRequest
    );
  }
  /**
   * Validates a card via webhook.
   * @param webhookRequest - The request payload for card validation
   * @returns Promise indicating the result of the validation
   */
  async validateCardWebhook(webhookRequest) {
    return this.httpClient.create(
      "/api/v1/card-payments/checkout-webhook/card-validation",
      webhookRequest
    );
  }
  /**
   * Handles disputes via webhook.
   * @param webhookRequest - The request payload for dispute handling
   * @returns Promise indicating the result of the dispute handling
   */
  async disputeWebhook(webhookRequest) {
    return this.httpClient.create(
      "/api/v1/card-payments/checkout-webhook/disputes",
      webhookRequest
    );
  }
  /**
   * Connects a bank via webhook.
   * @param webhookRequest - The request payload for bank connection
   * @returns Promise indicating the result of the bank connection
   */
  async connectBankWebhook(webhookRequest) {
    return this.httpClient.create("/api/v1/webhook/truelayer/bank-connect", webhookRequest);
  }
  // ------------------- Manual Transfer Deposit Methods -------------------
  /**
   * Processes a manual transfer deposit.
   * @param manualTransferRequest - The request payload for manual transfer deposit
   * @returns Promise containing the response with transfer details
   */
  async manualTransferDeposit(manualTransferRequest) {
    return this.httpClient.create(
      "/api/v1/manual-transfer/deposit",
      manualTransferRequest
    );
  }
  // ------------------- Manual Transfer Withdrawal Methods -------------------
  /**
   * Processes a manual transfer withdrawal.
   * @param manualTransferRequest - The request payload for manual transfer withdrawal
   * @returns Promise indicating the result of the withdrawal
   */
  async manualTransferWithdrawal(manualTransferRequest) {
    return this.httpClient.create(
      "/api/v1/manual-transfer/withdrawal",
      manualTransferRequest
    );
  }
  // ------------------- Manual Transfer Template Methods -------------------
  /**
   * Deletes a specific manual transfer template by its ID.
   * @param templateId - The ID of the template to delete
   * @returns Promise indicating the result of the deletion
   */
  async deleteManualTransferTemplate(templateId) {
    return this.httpClient.delete(
      `/api/v1/manual-transfer-template/${templateId}`
    );
  }
  // ------------------- Wallet Transfers Methods -------------------
  /**
   * Creates a pre-order for a wallet transfer.
   * @param preOrderRequest - The request payload for the wallet transfer pre-order
   * @returns Promise containing the response with pre-order details
   */
  async createWalletTransferPreOrder(preOrderRequest) {
    return this.httpClient.create("/api/v1/cash/wallets/transfers/pre-order", preOrderRequest);
  }
  /**
   * Creates a wallet transfer order.
   * @param orderRequest - The request payload for the wallet transfer order
   * @returns Promise containing the response with order details
   */
  async createWalletTransferOrder(orderRequest) {
    return this.httpClient.create("/api/v1/cash/wallets/transfers/order", orderRequest);
  }
  // ------------------- Payment Methods Methods -------------------
  /**
   * Retrieves the payment methods for the current customer.
   * @returns Promise containing the response with payment methods
   */
  async getPaymentMethods() {
    return this.httpClient.read(
      "/api/v1/customers/me/payment-methods"
    );
  }
  /**
   * Retrieves the withdrawal methods for the current customer.
   * @returns Promise containing the response with withdrawal methods
   */
  async getWithdrawMethods() {
    return this.httpClient.read(
      "/api/v1/customers/me/payment-methods/withdraw"
    );
  }
  /**
   * Retrieves the bank accounts associated with a specific bank ID.
   * @param bankId - The ID of the bank to retrieve accounts for
   * @returns Promise containing the response with bank accounts
   */
  async getBankAccounts(bankId) {
    return this.httpClient.read(
      `/api/v1/customers/me/payment-methods/banks/${bankId}/accounts`
    );
  }
  /**
   * Deletes a specific payment method by its ID.
   * @param paymentMethodId - The ID of the payment method to delete
   * @returns Promise indicating the result of the deletion
   */
  async deletePaymentMethod(paymentMethodId) {
    return this.httpClient.delete(
      `/api/v1/customers/me/payment-methods/${paymentMethodId}`
    );
  }
  /**
   * Updates the default payment method for the current customer.
   * @param paymentMethodId - The ID of the payment method to set as default
   * @param command - The command containing the update details
   * @returns Promise indicating the result of the update
   */
  async updateDefaultPaymentMethod(paymentMethodId, command) {
    return this.httpClient.update(
      `/api/v1/customers/me/payment-methods/${paymentMethodId}`,
      command
    );
  }
  /**
   * Checks the status of a specific payment method by its ID.
   * @param paymentMethodId - The ID of the payment method to check
   * @returns Promise containing the response with the payment method status
   */
  async checkPaymentMethodStatus(paymentMethodId) {
    return this.httpClient.read(
      `/api/v1/customers/me/payment-methods/${paymentMethodId}/status`
    );
  }
  // ------------------- Webhooks Methods -------------------
  /**
   * Handles the payments confirmation webhook from Truelayer.
   * @param webhookRequest - The request payload for the payments confirmation webhook
   * @returns Promise indicating the result of the webhook handling
   */
  async paymentsConfirmationWebhook() {
    return this.httpClient.create(
      "/api/v1/truelayer-webhook/payments-confirmation"
    );
  }
  // ------------------- Top Up Methods -------------------
  /**
   * Retrieves information about top-up options.
   * @returns Promise containing the response with top-up information
   */
  async getTopUpInfo() {
    return this.httpClient.read(
      "/api/v1/payments/top-up/info"
    );
  }
  /**
   * Processes a top-up payment.
   * @param topUpRequest - The request payload for the top-up payment
   * @returns Promise containing the response with top-up payment details
   */
  async topUpPayment(topUpRequest) {
    return this.httpClient.create(
      "/api/v2/payments/top-up/payment",
      topUpRequest
    );
  }
  // ------------------- Cashout Methods -------------------
  /**
   * Processes a cashout request to a linked bank account.
   * @param cashoutRequest - The request payload for the cashout
   * @returns Promise indicating the result of the cashout
   */
  async cashout(cashoutRequest) {
    return this.httpClient.create(
      "/api/v1/cashout/bank/linked-payout",
      cashoutRequest
    );
  }
  // ------------------- SEPA Transfer Methods -------------------
  /**
   * Processes a SEPA transfer request.
   * @param sepaRequest - The request payload for the SEPA transfer
   * @returns Promise containing the response with SEPA transfer details
   */
  async sepaTransfer(sepaRequest) {
    return this.httpClient.create(
      "/api/v1/agree-sepa-conditions",
      sepaRequest
    );
  }
  // ------------------- Banks Methods -------------------
  /**
   * Connects a bank account for the current customer.
   * @param bankConnectionRequest - The request payload for bank connection
   * @returns Promise containing the response with bank connection details
   */
  async connectBank(bankConnectionRequest) {
    return this.httpClient.create(
      "/api/v1/banks/connect",
      bankConnectionRequest
    );
  }
  /**
   * Retrieves a list of available banks.
   * @returns Promise containing the response with available banks
   */
  async getAvailableBanks() {
    return this.httpClient.read("/api/v1/banks");
  }
};
var CashService_default = CashService;

// src/services/portfolio/PortfolioService.ts
var PortfolioService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Retrieves portfolio performance
   * @returns Promise containing the portfolio performance response
   */
  async getPortfolioPerformance() {
    return this.httpClient.read(
      "/api/v1/portfolio/performance"
    );
  }
  /**
   * Retrieves portfolio performance V2 with multi-currency support
   * @returns Promise containing the enhanced portfolio performance response
   */
  async getPortfolioPerformanceV2() {
    return this.httpClient.read(
      "/api/v2/portfolio/performance"
    );
  }
  /**
   * Retrieves performance data for a specific holding
   * @param assetId - The ID of the asset to retrieve performance for
   * @returns Promise containing the holding performance response
   */
  async getHoldingPerformance(assetId) {
    return this.httpClient.read(
      `/api/v1/portfolio/performance/holding/${assetId}`
    );
  }
  /**
   * Retrieves performance data for a specific holding with multi-currency support
   * @param assetId - The ID of the asset to retrieve performance for
   * @returns Promise containing the enhanced holding performance response
   */
  async getHoldingPerformanceV2(assetId) {
    return this.httpClient.read(
      `/api/v2/portfolio/performance/holding/${assetId}`
    );
  }
  /**
   * Retrieves portfolio balance information
   * @returns Promise containing the portfolio balance response
   */
  async getPortfolioBalance() {
    return this.httpClient.read(
      "/api/v1/portfolio/balance"
    );
  }
  /**
   * Retrieves portfolio balance information with multi-wallet support
   * @returns Promise containing the enhanced portfolio balance response
   */
  async getPortfolioBalanceV2() {
    return this.httpClient.read(
      "/api/v2/portfolio/balance"
    );
  }
  /**
   * Retrieves the provider's portfolio ID
   * @returns Promise containing the provider portfolio ID response
   */
  async getProviderPortfolioId() {
    return this.httpClient.read("/api/v1/portfolio/provider");
  }
  /**
   * Retrieves portfolio valuations for a specific portfolio and date range
   * @param portfolioId - The ID of the portfolio
   * @param dateRange - Optional date range in format 'YYYY-MM-DD,YYYY-MM-DD'
   * @returns Promise containing the portfolio valuations response
   */
  async getPortfolioValuations(portfolioId, dateRange) {
    const url = dateRange ? `/api/v1/portfolio/${portfolioId}/valuation?date=${dateRange}` : `/api/v1/portfolio/${portfolioId}/valuation`;
    return this.httpClient.read(url);
  }
};
var PortfolioService_default = PortfolioService;

// src/services/ultrai/UltraiService.ts
var UltraiService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Creates a new thread
   * @returns Promise containing the thread response
   */
  async createThread() {
    return this.httpClient.create(
      "/api/v1/ultrai/threads"
    );
  }
  /**
   * Sends feedback for a specific message in a thread
   * @param threadId - The ID of the thread
   * @param messageId - The ID of the message
   * @param feedback - The feedback request data
   * @returns Promise containing the response
   */
  async sendFeedback(threadId, messageId, feedback) {
    return this.httpClient.patch(
      `/api/v1/ultrai/threads/${threadId}/messages/${messageId}/feedback`,
      feedback
    );
  }
  /**
   * Accepts terms and conditions
   * @returns Promise containing the response
   */
  async acceptTerms() {
    return this.httpClient.create(
      "/api/v1/ultrai/terms-and-conditions"
    );
  }
  /**
   * Checks terms and conditions for a specific customer
   * @param customerId - The ID of the customer
   * @returns Promise containing the response
   */
  async checkTerms(customerId) {
    return this.httpClient.read(
      `/api/v1/ultrai/terms-and-conditions/${customerId}`
    );
  }
  /**
   * Calls the chat entry point
   * @param requestData - The request data for the chat
   * @returns Promise containing the response
   */
  async chat(requestData) {
    return this.httpClient.create(
      "/api/v1/ultrai/chat",
      requestData
    );
  }
};
var UltraiService_default = UltraiService;

// src/services/customers/CustomersService.ts
var CustomerService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Creates a customer as the first step for the onboarding
   * @param registrationData - The data required to create a new customer
   * @returns Promise containing the login response
   */
  async createCustomer(registrationData) {
    return this.httpClient.create(
      "/api/v2/customers",
      registrationData
    );
  }
  /**
   * Creates a customer as the first step for the onboarding
   * @param registrationData - The data required to create a new customer
   * @returns Promise containing the login response
   * @deprecated Use createCustomer
   */
  async createCustomerV1(registrationData) {
    return this.httpClient.create(
      "/api/v1/customers",
      registrationData
    );
  }
  /**
   * Checks customer email availability
   * @param email - The email to check
   * @returns Promise containing the response. OK means 'Customer email found'
   */
  async checkCustomerEmail(email) {
    return this.httpClient.head(`/api/v1/customers?email=${email}`);
  }
  /**
   * Retrieves customer profile information
   * @returns Promise containing the customer profile
   */
  async getCustomerProfile() {
    return this.httpClient.read("/api/v1/customers/me/profile");
  }
  /**
   * Updates customer personal information
   * @param personalInfo - The data to update
   * @returns Promise containing no content response
   */
  async updateCustomerPersonalInfo(personalInfo) {
    return this.httpClient.patch(
      "/api/v1/customers/me/personal-info",
      personalInfo
    );
  }
  /**
   * Retrieves onboarding status
   * @returns Promise containing the onboarding status
   */
  async getOnboardingStatus() {
    return this.httpClient.read(
      "/api/v1/customers/me/onboarding-status"
    );
  }
  /**
   * Retrieves the customer contract by Document ID
   * @param documentId - The document ID to retrieve
   * @returns Promise containing the customer contract
   */
  async getCustomerContract(documentId) {
    return this.httpClient.read(
      `/api/v1/customers/me/contract/${documentId}`
    );
  }
  /**
   * Sends a request for a challenge verification
   * @param challengeType - The type of challenge request
   * @param contractId - The contract ID (optional)
   * @param acceptLanguage - The accepted language (optional)
   * @returns Promise containing the response
   * This endpoint has been corrected as acceptLanguage is not a real parameter for it
   */
  async sendChallengeVerification(challengeType, contractId, email) {
    return this.httpClient.patch(
      `/api/v1/customers/me/challenge-verification/send?challengeType=${challengeType}${contractId ? `&contractId=${contractId}` : ""}${email ? `&email=${email}` : ""}`
    );
  }
  /**
   * Verifies the code for a challenge verification previously sent
   * @param challengeType - The type of challenge request
   * @param contractId - The contract ID (optional)
   * @param verificationCode - The verification code to verify
   * @returns Promise containing no content response
   */
  async verifyChallengeVerification(challengeType, verificationCode, contractId) {
    return this.httpClient.create(
      `/api/v1/customers/me/challenge-verification/verify?challengeType=${challengeType}${contractId ? `&contractId=${contractId}` : ""}`,
      { verificationCode }
    );
  }
  /**
   * Retrieves the MIFID questionary
   * @returns Promise containing the MIFID questionary
   */
  async getMifidQuestionary() {
    return this.httpClient.read(
      "/api/v1/mifid-verification/questionary"
    );
  }
  /**
   * Updates customer with MIFID questionary choices
   * @param choices - The choices to update
   * @returns Promise containing no content response
   */
  async updateMifidChoices(choices) {
    return this.httpClient.create(
      "/api/v1/mifid-verification/choices",
      choices
    );
  }
  /**
   * Retrieves the assets of the user watchlist
   * @returns Promise containing the watchlisted assets
   */
  async getWatchlist() {
    return this.httpClient.read(
      "/api/v1/customers/me/stocks-watchlists"
    );
  }
  /**
   * Retrieves asset in watchlist
   * @param stockId - The stock ID to retrieve
   * @returns Promise containing the asset details
   */
  async getWatchlistAsset(stockId) {
    return this.httpClient.read(
      `/api/v1/customers/me/stocks-watchlists/${stockId}`
    );
  }
  /**
   * Adds the asset to the watchlist
   * @param stockId - The stock ID to add
   * @returns Promise containing the updated watchlist
   */
  async addToWatchlist(stockId) {
    return this.httpClient.create(
      `/api/v1/customers/me/stocks-watchlists/${stockId}`
    );
  }
  /**
   * Removes the asset from the watchlist
   * @param stockId - The stock ID to remove
   * @returns Promise containing the updated watchlist
   */
  async removeFromWatchlist(stockId) {
    return this.httpClient.delete(
      `/api/v1/customers/me/stocks-watchlists/${stockId}`
    );
  }
  // ... Add other methods as needed based on the customers.yaml ...
};
var CustomersService_default = CustomerService;

// src/services/documents/DocumentsService.ts
var DocumentsService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Fetches a transaction document by customer ID, document type, and document name
   * @param customerId - The ID of the customer
   * @param documentType - The type of document
   * @param documentName - The name of the document
   * @returns Promise containing the document response
   */
  async fetchTransactionDocument(customerId, documentType, documentName) {
    return this.httpClient.read(
      `/api/v1/documents/${customerId}/${documentType}/${documentName}`
    );
  }
  /**
   * Sends a transaction document via email
   * @param customerId - The ID of the customer
   * @param documentType - The type of document
   * @param documentName - The name of the document
   * @returns Promise containing the response of the email sending
   */
  async sendTransactionDocumentViaEmail(customerId, documentType, documentName) {
    return this.httpClient.create(
      `/api/v1/documents/${customerId}/${documentType}/${documentName}/send`
    );
  }
  /**
   * Fetches a static precontract PDF file
   * @returns Promise containing the static precontract PDF response
   */
  async fetchStaticPrecontractPdf() {
    return this.httpClient.read("/api/v1/documents/static/precontract");
  }
  /**
   * Sends a static precontract PDF file to the client's email
   * @returns Promise containing the response of the email sending
   */
  async sendStaticPrecontractPdf() {
    return this.httpClient.create(
      "/api/v1/documents/static/precontract/send"
    );
  }
};
var DocumentsService_default = DocumentsService;

// src/services/login/LoginService.ts
var LoginService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Authenticates an existing customer and returns a bearer token.
   * @param loginData - The credentials required to authenticate the customer
   * @returns Promise containing the login response
   */
  async login(loginData) {
    return this.httpClient.create(
      "/api/v1/auth/login",
      loginData
    );
  }
  /**
   * Authenticates an existing customer for web app and returns a 2FA challenge.
   * @param loginData - The credentials required to authenticate the customer
   * @returns Promise containing the challenge expiration time
   */
  async customer2faLogin(loginData) {
    return this.httpClient.create(
      "/api/v1/auth/login2fa",
      loginData
    );
  }
  /**
   * Sends the 2FA code for verification.
   * @param confirmationData - The email and challenge code for verification
   * @returns Promise containing the login response
   */
  async customer2fa(confirmationData) {
    return this.httpClient.create(
      "/api/v1/auth/2fa",
      confirmationData
    );
  }
  /**
   * Renews tokens during the grace period using the renewal token.
   * @param renewalData - The renewal token required to renew the session
   * @returns Promise containing the renewal response
   */
  async tokenRenewal(renewalData) {
    return this.httpClient.create(
      "/api/v1/auth/renew",
      renewalData
    );
  }
  /**
   * Requests a reset password by sending an email to the customer.
   * @param resetRequest - The email of the customer to reset the password
   * @returns Promise containing the response
   */
  async resetPassword(resetRequest) {
    return this.httpClient.create(
      "/api/v1/auth/reset-password/send",
      resetRequest
    );
  }
  /**
   * Verifies the reset password challenge and changes the password.
   * @param verificationData - The email, new password, and verification code
   * @returns Promise containing no content response
   */
  async verifyResetPassword(verificationData) {
    return this.httpClient.create(
      "/api/v2/auth/reset-password/verify",
      verificationData
    );
  }
};
var LoginService_default = LoginService;

// src/utils/helpers.ts
var buildQueryParams = (params) => {
  return Object.keys(params || {}).reduce(
    (acc, item, idx) => {
      if (!params[item]?.toString()) {
        return acc;
      }
      return `${acc}${idx === 0 ? "?" : "&"}${item}=${params[item]}`;
    },
    ""
  );
};

// src/services/mobileApi/MobileApiService.ts
var MobileApiService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Retrieves 3 assets of each type and total elements.
   * @param filter - Optional filter by asset name.
   * @returns A promise that resolves to AssetsDiscover.
   */
  async discoverAssets(filter) {
    return this.httpClient.read(
      `/api/v1/mobile/assets/discover${filter ? "?filter=" + filter : ""}`
    );
  }
  /**
   * Retrieves 3 assets of each type and total elements (v2).
   * @param filter - Optional filter by asset name.
   * @returns A promise that resolves to AssetsDiscoverV2.
   */
  async discoverAssetsV2(filter) {
    return this.httpClient.read(
      `/api/v2/mobile/assets/discover${filter ? "?filter=" + filter : ""}`
    );
  }
  /**
   * Retrieves available currencies exchanges from Forex.
   * @param target - Filter by target type.
   * @returns A promise that resolves to AvailableCurrenciesResponse.
   */
  async getCurrencyExchanges(target) {
    return this.httpClient.read(
      `/api/v1/mobile/currencies-exchanges?target=${target}`
    );
  }
  /**
   * Retrieve tax information.
   * @param locale - Language in which tax information is returned (header param).
   * @returns A promise that resolves to string containing tax information URL.
   */
  async retrieveTaxInfo() {
    return this.httpClient.read(`/api/v1/mobile/taxes/preview`);
  }
  /**
   * Retrieve customer data related to tier.
   * @returns A promise that resolves to TierDataResponse.
   */
  async getTierData() {
    return this.httpClient.read(`/api/v1/mobile/tier/data`);
  }
  /**
   * Retrieves watch lists of the assets.
   * @param page - The page number for pagination.
   * @param size - The size of items per page.
   * @returns A promise that resolves to FetchAssetsWatchlistPaginatedResponse.
   */
  async getWatchlist(page, size) {
    return this.httpClient.read(
      `/api/v2/mobile/customers/me/stocks-watchlist?page=${page}&size=${size}`
    );
  }
  /**
   * Retrieves the assets of specified type.
   * @param params - Various parameters for filtering assets.
   * @returns A promise that resolves to FetchAssetsResponse.
   */
  async fetchAssets(params) {
    const newParams = buildQueryParams(
      params
    );
    return this.httpClient.read(
      `/api/v2/mobile/assets${newParams}`
    );
  }
  /**
   * Retrieves all the assets allowed Filters.
   * @returns A promise that resolves to FetchAssetsFiltersResponse.
   */
  async fetchAssetsFilters() {
    return this.httpClient.read(
      `/api/v2/mobile/assets/filters`
    );
  }
  /**
   * Retrieves the asset details.
   * @param assetId - The asset identifier.
   * @returns A promise that resolves to FetchAssetDetailsResponse.
   */
  async fetchAssetDetails(assetId) {
    return this.httpClient.read(
      `/api/v2/mobile/assets/${assetId}`
    );
  }
  /**
   * Create a pre-order for an asset.
   * @param request - The request object for creating a pre-order.
   * @returns A promise that resolves to AssetPreOrderResponse.
   */
  async createAssetPreOrder(request) {
    return this.httpClient.create(
      `/api/v2/mobile/assets/pre-orders`,
      request
    );
  }
  /**
   * Create an order for an asset.
   * @param request - The request object for creating an order.
   * @returns A promise that resolves to AssetOrderResponse.
   */
  async createAssetOrder(request) {
    return this.httpClient.create(
      `/api/v2/mobile/assets/orders`,
      request
    );
  }
  /**
   * Creates wallet transfers preorders automatically.
   * @param preOrderId - The ID of the preorder.
   * @returns A promise that resolves to PreOrderWithTransfersResponse.
   */
  async createWalletTransfersPreorders(preOrderId) {
    return this.httpClient.create(
      `/api/v1/mobile/pre-orders/${preOrderId}/required-transfers`
    );
  }
  /**
   * Create multiple orders given a list of pre orders id and multiple wallet transfers.
   * @param request - The request object containing preOrdersId and walletTransferPreOrdersId.
   * @returns A promise that resolves to OrderResponse.
   */
  async createMultipleOrders(request) {
    return this.httpClient.create(
      `/api/v1/mobile/orders`,
      request
    );
  }
};
var MobileApiService_default = MobileApiService;

// src/services/onboardingComplyVerify/OnboardingComplyVerifyService.ts
var OnboardingComplyVerifyService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  // Onboarding Methods
  /**
   * Creates a new customer.
   * @param registrationData - The minimal required info for creating a customer and starting the onboarding process.
   * @returns Promise containing the login response
   */
  async signUp(registrationData) {
    return this.httpClient.create(
      "/api/v1/onboarding/sign-up",
      registrationData
    );
  }
  /**
   * Sends a request for a challenge verification.
   * @returns Promise containing the challenge send response
   */
  async sendEmailChallenge() {
    return this.httpClient.create(
      "/api/v1/onboarding/steps/email/challenge/send"
    );
  }
  /**
   * Verifies the code for a challenge verification previously sent.
   * @param verificationData - The verification code data
   * @returns Promise with no content
   */
  async verifyEmailChallenge(verificationData) {
    return this.httpClient.create(
      "/api/v1/onboarding/steps/email/challenge/verify",
      verificationData
    );
  }
  /**
   * Sends a request for a phone challenge verification.
   * @param phoneData - The phone number data
   * @returns Promise containing the challenge send response
   */
  async sendPhoneChallenge(phoneData) {
    return this.httpClient.create(
      "/api/v1/onboarding/steps/phone/challenge/send",
      phoneData
    );
  }
  /**
   * Verifies the code for a phone challenge verification previously sent.
   * @param verificationData - The verification code data
   * @returns Promise with no content
   */
  async verifyPhoneChallenge(verificationData) {
    return this.httpClient.create(
      "/api/v1/onboarding/steps/phone/challenge/verify",
      verificationData
    );
  }
  /**
   * Requests a new KYC session for the authenticated user.
   * @returns Promise containing the KYC session response
   */
  async createKycSession() {
    return this.httpClient.create("/api/v1/onboarding/steps/kyc/session");
  }
  /**
   * Retrieves the countries that the user can use to fill their address.
   * @returns Promise containing the countries response
   */
  async getKycCountries() {
    return this.httpClient.read(
      "/api/v1/onboarding/steps/kyc/countries"
    );
  }
  /**
   * Gets personal info from verification for later confirmation.
   * @returns Promise containing the personal info response
   */
  async getKycPersonalInfo() {
    return this.httpClient.read(
      "/api/v1/onboarding/steps/kyc/personal-info"
    );
  }
  /**
   * Confirms the identity of the user performing the onboarding and updates various info.
   * @param personalInfo - The personal info data
   * @returns Promise with no content
   */
  async confirmIdentity(personalInfo) {
    return this.httpClient.update(
      "/api/v1/onboarding/steps/kyc/confirm-identity",
      personalInfo
    );
  }
  /**
   * Retrieves the MIFID questionnaire.
   * @returns Promise containing the MIFID questionnaire response
   */
  async getMifidQuestionnaire() {
    return this.httpClient.read(
      "/api/v1/onboarding/steps/mifid"
    );
  }
  /**
   * Updates customer onboarding with MIFID questionnaire choices.
   * @param choices - The MIFID choices data
   * @returns Promise with no content
   */
  async updateMifidChoices(choices) {
    return this.httpClient.update(
      "/api/v1/onboarding/steps/mifid",
      choices
    );
  }
  /**
   * Uploads a bank statement.
   * @param bankStatement - The bank statement file
   * @returns Promise with no content
   */
  async uploadBankStatement(bankStatement) {
    return this.httpClient.create(
      "/api/v1/onboarding/steps/bank-statement",
      bankStatement
    );
  }
  /**
   * Retrieves and accepts agreements and consents.
   * @returns Promise containing the agreements response
   */
  async getAgreements() {
    return this.httpClient.read(
      "/api/v1/onboarding/steps/agreements"
    );
  }
  /**
   * Upserts agreement acceptance.
   * @param agreements - The accepted agreements data
   * @returns Promise with no content
   */
  async upsertAgreements(agreements) {
    return this.httpClient.update(
      "/api/v1/onboarding/steps/agreements",
      agreements
    );
  }
  /**
   * Retrieves the status of the onboarding process.
   * @returns Promise containing the onboarding status response
   */
  async getOnboardingStatus() {
    return this.httpClient.read("/api/v1/onboarding/status");
  }
  // Webhook Methods
  /**
   * Handles KYC events via webhook.
   * @returns Promise with no content
   */
  async handleKycEvent() {
    return this.httpClient.create("/api/v2/kyc-webhook/event");
  }
  /**
   * Handles KYC verification decisions via webhook.
   * @returns Promise with no content
   */
  async handleKycDecision() {
    return this.httpClient.create("/api/v2/kyc-webhook/decision");
  }
  /**
   * Handles KYC events via webhook for version 3.
   * @returns Promise with no content
   */
  async handleKycEventV3() {
    return this.httpClient.create("/api/v3/kyc-webhook/event");
  }
  /**
   * Handles KYC verification decisions via webhook for version 3.
   * @returns Promise with no content
   */
  async handleKycDecisionV3() {
    return this.httpClient.create("/api/v3/kyc-webhook/decision");
  }
  /**
   * Handles AML decisions via webhook for version 2.
   * @returns Promise with no content
   */
  async handleAmlDecisionV2() {
    return this.httpClient.create("/api/v2/aml-webhook/decision");
  }
  /**
   * Handles AML decisions via webhook for version 3.
   * @returns Promise with no content
   */
  async handleAmlDecisionV3() {
    return this.httpClient.create("/api/v3/aml-webhook/decision");
  }
  // KYC Methods
  /**
   * Fetches KYC information for the authenticated customer profile.
   * @returns Promise containing the KYC information response
   */
  async getCustomerKycInfo() {
    return this.httpClient.read(
      "/api/v1/customers/me/kyc-info"
    );
  }
  /**
   * Requests a new KYC session for the authenticated user.
   * @returns Promise containing the KYC session response
   */
  async verifyKycSession() {
    return this.httpClient.create("/api/v1/kyc-verifications/sessions");
  }
  /**
   * Verifies KYC personal data from the customer.
   * @param personalData - The personal data to verify
   * @returns Promise with no content
   */
  async verifyKycData() {
    return this.httpClient.update(
      "/api/v1/kyc-verifications/verify",
      void 0
    );
  }
};
var OnboardingComplyVerifyService_default = OnboardingComplyVerifyService;

// src/services/stocks/StocksService.ts
var StocksService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Retrieves all stock collections
   * @returns Promise containing the stock collections response
   */
  async getAllCollections() {
    return this.httpClient.read(
      "/api/v1/stock-collections"
    );
  }
  /**
   * Retrieves a specific stock collection by ID
   * @param collectionId - The ID of the collection to retrieve
   * @returns Promise containing the stock collection details
   */
  async getOneCollection(collectionId) {
    return this.httpClient.read(
      `/api/v1/stock-collections/${collectionId}`
    );
  }
  /**
   * Retrieves all available filters for assets
   * @returns Promise containing the asset filters response
   */
  async availableAssetFiltersV3() {
    return this.httpClient.read("/api/v3/assets/filters");
  }
  /**
   * Retrieves all available filters for assets (v2)
   * @returns Promise containing the asset filters response
   */
  async availableAssetFiltersV2(assetType) {
    const url = assetType ? `/api/v2/assets/filters?assetType=${assetType}` : "/api/v2/assets/filters";
    return this.httpClient.read(url);
  }
  /**
   * Retrieves all options available for a specific filter
   * @param filterId - The ID of the filter to retrieve options for
   * @returns Promise containing the filter options
   */
  async assetFilterOptions(filterId) {
    return this.httpClient.read(
      `/api/v1/assets/filters/${filterId}`
    );
  }
  /**
   * Retrieves all saving plans
   * @returns Promise containing the saving plans response
   */
  async retrieveSavingPlans(assetManager) {
    const url = assetManager ? `/api/v1/assets/saving-plans?assetManager=${assetManager}` : "/api/v1/assets/saving-plans";
    return this.httpClient.read(url);
  }
  /**
   * Retrieves all available stocks
   * @param params - Optional parameters to include in the request
   * @returns Promise containing the stocks response
   */
  async retrieveStocks(params) {
    const queryParams = buildQueryParams(
      params
    );
    return this.httpClient.read(`/api/v1/stocks${queryParams}`);
  }
  /**
   * Retrieves details for a specific stock by ID
   * @param stockId - The ID of the stock to retrieve
   * @param params - Optional parameters to include in the request
   * @returns Promise containing the stock details
   */
  async retrieveStockInfo(stockId, params) {
    const queryParams = buildQueryParams(
      params
    );
    return this.httpClient.read(
      `/api/v1/stocks/${stockId}${queryParams}`
    );
  }
  /**
   * Retrieves stock details for a specific stock in the market session
   * @param stockId - The ID of the stock to retrieve
   * @param params - Optional parameters to include in the request
   * @returns Promise containing the stock extended details
   */
  async retrieveStockMarketSessionInfo(stockId, params) {
    const queryParams = buildQueryParams(
      params
    );
    return this.httpClient.read(
      `/api/v2/stocks/${stockId}${queryParams}`
    );
  }
  /**
   * Retrieves the stock chart for a specific stock
   * @param stockId - The ID of the stock to retrieve the chart for
   * @param period - Period in which stock prices are shown
   * @returns Promise containing the stock chart information
   */
  async retrieveStockChart(stockId, period) {
    return this.httpClient.read(
      `/api/v1/stocks/${stockId}/chart${period ? "?period=" + period : ""}`
    );
  }
  /**
   * Retrieves company information for a specific stock
   * @param stockId - The ID of the stock to retrieve company info for
   * @returns Promise containing the company information
   */
  async retrieveCompanyInfo(stockId) {
    return this.httpClient.read(
      `/api/v1/stocks/${stockId}/company-info`
    );
  }
  /**
   * Retrieves liquidity information for a specific stock
   * @param stockId - The ID of the stock to retrieve liquidity info for
   * @returns Promise containing the liquidity information
   */
  async retrieveLowLiquidity(stockId) {
    return this.httpClient.read(
      `/api/v1/stocks/${stockId}/liquidity`
    );
  }
};
var StocksService_default = StocksService;

// src/services/transactions/TransactionsService.ts
var TransactionsService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  // Orders Methods
  /**
   * Retrieves a list of orders
   * @param params - Optional parameters for pagination and filtering
   * @returns Promise containing the paginated order response
   */
  async getOrders(params) {
    const queryParams = buildQueryParams(
      params
    );
    return this.httpClient.read(
      `/api/v2/transactions/orders${queryParams}`
    );
  }
  /**
   * Retrieves details of a specific order by ID
   * @param orderId - The ID of the order to retrieve
   * @returns Promise containing the order details response
   */
  async getOrderById(orderId) {
    return this.httpClient.read(
      `/api/v2/transactions/orders/${orderId}`
    );
  }
  // Transactions Methods
  /**
   * Retrieves a list of transactions
   * @returns Promise containing the paginated transaction response
   */
  async getTransactions(params) {
    const queryParams = buildQueryParams(
      params
    );
    return this.httpClient.read(
      `/api/v2/transactions${queryParams}`
    );
  }
  /**
   * Retrieves details of a specific dividend transaction by ID
   * @param transactionId - The ID of the dividend transaction to retrieve
   * @param dividendType - The type of dividend transaction
   * @returns Promise containing the dividend transaction details response
   */
  async getDividenTransactionById(transactionId, dividendType) {
    return this.httpClient.read(
      `/api/v1/transactions/dividend/${transactionId}?dividendType=${dividendType}`
    );
  }
  /**
   * Retrieves details of a specific deposit transaction by ID
   * @param transactionId - The ID of the deposit transaction to retrieve
   * @returns Promise containing the deposit transaction details response
   */
  async getDepositTransactionById(transactionId) {
    return this.httpClient.read(
      `/api/v1/transactions/deposit/${transactionId}`
    );
  }
  /**
   * Retrieves details of a specific withdrawal transaction by ID
   * @param transactionId - The ID of the withdrawal transaction to retrieve
   * @returns Promise containing the withdrawal transaction details response
   */
  async getWithdrawalTransactionById(transactionId) {
    return this.httpClient.read(
      `/api/v1/transactions/withdrawal/${transactionId}`
    );
  }
  /**
   * Retrieves details of a specific fund transaction by ID
   * @param transactionId - The ID of the fund transaction to retrieve
   * @returns Promise containing the fund transaction details response
   */
  async getFundTransactionById(transactionId) {
    return this.httpClient.read(
      `/api/v1/transactions/funds/${transactionId}`
    );
  }
  /**
   * Retrieves details of a specific safekeeping transaction by ID
   * @param transactionId - The ID of the safekeeping transaction to retrieve
   * @returns Promise containing the safekeeping transaction details response
   */
  async getSafekeepingTransactionById(transactionId) {
    return this.httpClient.read(
      `/api/v1/transactions/safekeepingfees/${transactionId}`
    );
  }
  /**
   * Retrieves details of a specific wallet transaction by ID
   * @param transactionId - The ID of the wallet transaction to retrieve
   * @returns Promise containing the wallet transaction details response
   */
  async getWalletTransactionById(transactionId) {
    return this.httpClient.read(
      `/api/v1/transactions/wallets/${transactionId}`
    );
  }
  /**
   * Retrieves details of a specific notes transaction by ID
   * @param transactionId - The ID of the notes transaction to retrieve
   * @returns Promise containing the notes transaction details response
   */
  async getNotesTransactionById(transactionId) {
    return this.httpClient.read(
      `/api/v1/transactions/notes/${transactionId}`
    );
  }
  // Filters Method
  /**
   * Retrieves available filters for transactions
   * @returns Promise containing the transaction filters response
   */
  async getTransactionFilters(status) {
    return this.httpClient.read(
      `/api/v1/transactions/filters?transactionStatus=${status}`
    );
  }
};
var TransactionsService_default = TransactionsService;

// src/services/translator/TranslatorService.ts
var TranslatorService = class extends BaseService_default {
  constructor(config) {
    super(config);
  }
  /**
   * Sends a translation request to the API
   * @param data - Translation request data
   * @returns Promise containing the translation response
   */
  async translate(data) {
    return this.httpClient.create(
      "/api/v1/translation",
      data
    );
  }
  /**
   * Retrieves translations for a specific language
   * @param language - Language code to fetch translations for
   * @returns Promise containing the language-specific translation response
   */
  async getTranslationByLanguage(language) {
    return this.httpClient.read(
      `/api/v1/translation/${language}`
    );
  }
};
var TranslatorService_default = TranslatorService;

// src/services/HeytradeApiSDK.ts
var HeytradeApiSDK = class {
  static initialize(config) {
    return {
      assessments: new AssessmentsService_default(config),
      assets: new AssetsService_default(config),
      brokerage: new BrokerageService_default(config),
      cash: new CashService_default(config),
      customers: new CustomersService_default(config),
      documents: new DocumentsService_default(config),
      login: new LoginService_default(config),
      mobileApi: new MobileApiService_default(config),
      onboardingComplyVerify: new OnboardingComplyVerifyService_default(config),
      portfolio: new PortfolioService_default(config),
      stocks: new StocksService_default(config),
      transactions: new TransactionsService_default(config),
      translator: new TranslatorService_default(config),
      ultrai: new UltraiService_default(config)
    };
  }
};
var HeytradeApiSDK_default = HeytradeApiSDK;
export {
  ChartType,
  ConditionalType,
  HeytradeApiSDK_default as HeytradeApiSDK,
  OrderMethod,
  PeriodEnum,
  Status,
  TransactionType,
  UltraiService_default as UltraiService
};
